import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Buildings, CaretDown, MagnifyingGlass } from "@phosphor-icons/react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useWorkspaces } from "@/lib/queries";
import { workspacePath, workspaceKeyFromPath } from "@/lib/workspaceNav";
import { getRecents } from "@/lib/recents";
import { OrgLogo } from "@/components/BrandLogo";

/**
 * Topbar client switcher — a fast, filterable jump-to-client dropdown so
 * consultants can hop between client workspaces without leaving the page.
 * When viewing a workspace, the trigger becomes an active-workspace chip
 * showing the current client's name + sector.
 */
export function ClientSwitcher() {
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const { data } = useWorkspaces({ page: 1, pageSize: 50, filters: { status: "Active" } });
  const items = data?.items || [];

  const activeKey = workspaceKeyFromPath(location.pathname);
  const active = activeKey ? items.find((w) => w.id === activeKey || w.slug === activeKey) : null;

  const recentIds = getRecents().filter((r) => r.type === "workspace").map((r) => r.id);
  const filtered = items.filter((w) =>
    !q.trim() || `${w.name} ${w.client_name || ""} ${w.sector || ""}`.toLowerCase().includes(q.toLowerCase())
  );
  const recents = filtered.filter((w) => recentIds.includes(w.id))
    .sort((a, b) => recentIds.indexOf(a.id) - recentIds.indexOf(b.id));
  const rest = filtered.filter((w) => !recentIds.includes(w.id));

  const go = (w) => { setOpen(false); setQ(""); navigate(workspacePath(w)); };

  const Row = ({ w }) => (
    <button key={w.id} data-testid={`client-switch-${w.id}`} onClick={() => go(w)}
      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-foreground/5">
      <span className="shrink-0"><OrgLogo name={w.client_name || w.name} website={w.website} logoUrl={w.logo_data_url} size={24} /></span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-foreground">{w.name}</span>
        {w.sector && <span className="block truncate text-[11px] text-muted-foreground">{w.sector}</span>}
      </span>
    </button>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {active ? (
          <button data-testid="client-switcher-trigger"
            className="hidden max-w-[120px] items-center gap-2 border border-border bg-surface px-2.5 py-1.5 text-sm text-foreground transition-colors hover:border-primary sm:flex xl:max-w-[240px]">
            <span aria-hidden="true" className="shrink-0"><OrgLogo name={active.client_name || active.name} website={active.website} logoUrl={active.logo_data_url} size={20} /></span>
            <span className="min-w-0 text-left leading-tight">
              <span className="block truncate font-semibold">{active.name}</span>
              {active.sector && <span className="hidden truncate font-mono text-[9px] uppercase tracking-wide text-muted-foreground xl:block">{active.sector}</span>}
            </span>
            <CaretDown className="h-3 w-3 shrink-0 text-muted-foreground" weight="bold" />
          </button>
        ) : (
          <button data-testid="client-switcher-trigger"
            className="hidden items-center gap-1.5 border border-border bg-surface px-3 py-1.5 text-sm text-foreground/80 transition-colors hover:border-primary hover:text-foreground sm:flex">
            <Buildings className="h-4 w-4 text-muted-foreground" weight="bold" />
            <span>Clients</span>
            <CaretDown className="h-3 w-3 text-muted-foreground" weight="bold" />
          </button>
        )}
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 rounded-none p-0" data-testid="client-switcher-panel">
        <div className="flex items-center gap-2 border-b border-border px-3 py-2">
          <MagnifyingGlass className="h-4 w-4 text-muted-foreground" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Jump to client…"
            data-testid="client-switcher-input" className="w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground" />
        </div>
        <div className="max-h-72 overflow-y-auto py-1">
          {recents.length > 0 && (
            <>
              <p className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">Recent</p>
              {recents.map((w) => <Row key={w.id} w={w} />)}
              {rest.length > 0 && <p className="px-3 pb-1 pt-2 font-mono text-[10px] uppercase tracking-wide text-muted-foreground">All clients</p>}
            </>
          )}
          {rest.map((w) => <Row key={w.id} w={w} />)}
          {filtered.length === 0 && <p className="px-3 py-4 text-center text-sm text-muted-foreground">No clients found.</p>}
        </div>
      </PopoverContent>
    </Popover>
  );
}
